import {
  IconButton,
  Text,
  DataTableColumnMenuButton,
  createDataTableColumnVisibility,
  type DataTableColumnVisibility,
} from "@carbon6/c6-frontend-design";
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { canModifyEmbodiment, type MePermissions, type UserRole } from "@/app";
import { hatcheryTextModes } from "@/app/theme";
import { ResourceBrowserLayout } from "@/components/browse";
import { resolveEmbodimentErrorMessage } from "@/components/embodiment";
import { createPartsColumns } from "../partsColumns";
import { getPartApi, partKindMetas, type PartFormValues, type PartKind } from "../partsCatalog";
import { PartsTableSource } from "../PartsTableSource";
import type { PartsTableRow } from "../PartsTableRow";
import type { PartDialogState } from "../EmbodimentPageTypes";
import { HatTextModal } from "@/components/modal";
import { HatDataTable } from "@/components/table/HatDataTable";
import { PartFormDialog } from "./PartFormDialog";
import styles from "../EmbodimentPage.module.css";
import { tableStripError } from "@/components/table/tableStateMessages";

const defaultPageSize = 20;

type PartsCatalogTabProps = {
  kind: PartKind;
  role: UserRole | null;
  // 서버가 산출한 삭제 권한. 받지 못했으면 null 이고 등급으로 폴백한다. 부품 삭제 버튼 표시에 쓴다.
  permissions: MePermissions | null;
};

// 부품 카탈로그 탭(EndEffector/Tip). 목록, 생성, 편집, 삭제를 다룬다. 종류는 prop 으로 고정한다.
// 부모가 key={kind} 를 주므로, 종류가 바뀌면 remount 되고 상태가 초기화된다.
export function PartsCatalogTab({ kind, role, permissions }: PartsCatalogTabProps) {
  const meta = partKindMetas[kind];
  const canModify = canModifyEmbodiment(role);
  const columns = useMemo(() => createPartsColumns(meta), [meta]);
  const [dialog, setDialog] = useState<PartDialogState>({ kind: "closed" });
  const [columnVisibility, setColumnVisibility] = useState<DataTableColumnVisibility>(() =>
    createDataTableColumnVisibility(columns),
  );

  const source = useMemo(
    () =>
      new PartsTableSource({
        kind,
        pageSize: defaultPageSize,
        rowClickHandler: (row: PartsTableRow) =>
          setDialog({
            kind: "form",
            mode: "edit",
            part: row.toRecord(),
            errorMessage: null,
            submitting: false,
          }),
        resolveErrorMessage: resolveEmbodimentErrorMessage,
      }),
    [kind],
  );

  useEffect(() => {
    void source.initialize();
    return () => source.dispose();
  }, [source]);

  const snapshot = useSyncExternalStore(source.subscribe, source.getSnapshot);
  const { loadState, page, rows, size, total } = snapshot;
  const isLoading = loadState === "loading";
  const firstItem = total === 0 ? 0 : (page - 1) * size + 1;
  const lastItem = Math.min(page * size, total);

  function closeDialog() {
    setDialog({ kind: "closed" });
  }

  async function handleFormSubmit(values: PartFormValues) {
    if (dialog.kind !== "form" || dialog.submitting) {
      return;
    }
    const current = dialog;
    setDialog({ ...current, submitting: true, errorMessage: null });
    try {
      const api = getPartApi(kind);
      if (current.mode === "create") {
        await api.create(values, { role });
        setDialog({ kind: "closed" });
        await source.goToPage(1);
      } else if (current.part) {
        const updated = await api.update(current.part.uuid, values, { role });
        source.replaceRow(source.createRow(updated));
        setDialog({ kind: "closed" });
      }
    } catch (error) {
      setDialog({
        ...current,
        submitting: false,
        errorMessage: resolveEmbodimentErrorMessage(error),
      });
    }
  }

  async function handleDeleteConfirm() {
    if (dialog.kind !== "delete" || dialog.submitting) {
      return;
    }
    const target = dialog.part;
    setDialog({ ...dialog, submitting: true, errorMessage: null });
    try {
      await getPartApi(kind).remove(target.uuid, { role });
      source.removeRow(target.uuid);
      setDialog({ kind: "closed" });
      await source.refresh();
    } catch (error) {
      setDialog({
        kind: "delete",
        part: target,
        submitting: false,
        errorMessage: resolveEmbodimentErrorMessage(error),
      });
    }
  }

  return (
    <>
      <ResourceBrowserLayout
        ariaLabel={`${meta.plural} 목록`}
        summaryCount={total}
        summaryLabel={meta.plural.toLowerCase()}
        actions={
          <>
            <IconButton
              text={`새 ${meta.singular}`}
              variant="solid"
              disabled={!canModify || isLoading}
              onClick={() =>
                setDialog({
                  kind: "form",
                  mode: "create",
                  part: null,
                  errorMessage: null,
                  submitting: false,
                })
              }
            />
            <IconButton
              text={isLoading ? "로딩 중…" : "새로고침"}
              variant="surface"
              disabled={isLoading}
              onClick={() => void source.refresh()}
            />
          </>
        }
        notice={
          canModify ? null : `${meta.singular} 생성, 수정, 삭제는 admin 권한에서만 가능합니다.`
        }
        error={tableStripError(snapshot)}
        onRetry={() => void source.refresh()}
        footer={
          <div className={styles.tableFooter} aria-label={`${meta.plural} 테이블 컨트롤`}>
            {rows.length > 0 ? (
              <Text variant={hatcheryTextModes["body-normal"]} className={styles.range}>
                총 {total}개 중 {firstItem}-{lastItem}
              </Text>
            ) : (
              <span aria-hidden="true" />
            )}
            <DataTableColumnMenuButton
              columns={columns}
              columnVisibility={columnVisibility}
              menuPlacement="top"
              onColumnVisibilityChange={setColumnVisibility}
            />
          </div>
        }
      >
        <HatDataTable
          source={source}
          columns={columns}
          columnVisibility={columnVisibility}
          emptyMessage={`등록된 ${meta.plural} 항목이 없습니다. 새 ${meta.singular} 항목을 추가해 주세요.`}
          loadingMessage={`${meta.plural} 목록을 불러오고 있습니다.`}
          ariaLabel={`${meta.plural} 목록`}
          scrollable
          minWidth="720px"
        />
      </ResourceBrowserLayout>

      {dialog.kind === "form" ? (
        <PartFormDialog
          dialog={dialog}
          meta={meta}
          role={role}
          permissions={permissions}
          onClose={closeDialog}
          onSubmit={(values) => void handleFormSubmit(values)}
          onDelete={() =>
            dialog.part
              ? setDialog({
                  kind: "delete",
                  part: dialog.part,
                  errorMessage: null,
                  submitting: false,
                })
              : undefined
          }
        />
      ) : null}

      {dialog.kind === "delete" ? (
        <HatTextModal
          open
          eyebrow={`${meta.singular} · ${dialog.part.name}`}
          label={`선택한 ${meta.singular} 항목을 삭제합니다.`}
          caption={`이 ${meta.singular} 항목을 참조하는 Embodiment가 있으면 서버가 삭제를 거부할 수 있습니다.`}
          error
          errorMessage={dialog.errorMessage ?? undefined}
          busy={dialog.submitting}
          okText="삭제"
          onOkPress={() => void handleDeleteConfirm()}
          onCancelPress={closeDialog}
        />
      ) : null}
    </>
  );
}
